/**
 * The collector's front page, decided: four counts over everything they
 * hold, a strip of one collection's works, and what has happened lately.
 *
 * Pure. Collections, works and merged feeds go in; tiles, cards and activity
 * rows come out. The copy and limits are `dashboard.constants.ts`; the reads
 * and the chosen collection are `useDashboardController.ts`. A `StripCard` is
 * thinner than the collection screen's `WorkCard` on purpose — this is a way
 * back into the collection, not a second place claims are made about a work.
 *
 * Two refusals a reader would otherwise reach for: there is no valuation tile
 * and no 30-day delta, because the registry holds no prices; and the strip is
 * "the first few", not "the most recent", because a `WorkSummary` carries no
 * timestamp to sort on. Relative ages are read from `now` at render, so the
 * model takes a clock and the audit log's model, which refuses them, does not.
 */

import type { ActivityEntry, CollectionSummary, WorkSummary } from "@ondo/shared";

import { ACTIVITY_BY, describeAct } from "../lib/activityCopy.ts";
import type { ChoiceGroup } from "../lib/choices.ts";
import { LOCATION_LABELS } from "../lib/constants.ts";
import type { Tile } from "../lib/Tiles.tsx";
import { activityHref } from "../routes/routes.model.ts";
import { DASHBOARD_COPY } from "./dashboard.constants.ts";

// The shape moved to `lib/Tiles.tsx` when the agent's list grew a row of the
// same object. Re-exported so nothing that already imported it from here had to
// change to say the same thing.
export type { Tile };

/**
 * A work as the collection strip shows it: a photograph and a catalogue line.
 *
 * Deliberately thinner than the collection screen's `WorkCard` — no badge, no
 * location, no owner. This is a way back into the collection, not a second
 * place the registry makes claims about a work.
 */
export interface StripCard {
  id: string;
  href: string;
  title: string;
  /** "Erwin Wurm · 2001", or just the name when no year is recorded. */
  attribution: string;
  medium: string | null;
  /** A signed URL, or null when there is no photograph or its bytes are not ours to read. */
  imageUrl: string | null;
}

export interface ActivityRow {
  id: string;
  title: string;
  /** "Fat Car · 2 days ago" — what it was about and how long ago, as one line. */
  detail: string;
  glyph: string;
  /** Where the row goes, or null when its subject has no screen of its own. */
  href: string | null;
}

/** A work paired with the collection it was fetched from. */
export interface PlacedWork {
  work: WorkSummary;
  collectionId: string;
}

/**
 * The panel's switcher, as one ungrouped run.
 *
 * A `ChoiceGroup[]` for a list that has no groups, because the head that
 * renders it is `lib/CollectionHead.tsx` and the collection grid feeds the same
 * component groups of its own. One shape, one control, no mapping between them
 * to drift.
 */
export function collectionChoices(collections: CollectionSummary[]): ChoiceGroup[] {
  return [
    {
      label: null,
      choices: collections.map((collection) => ({
        key: collection.id,
        label: collection.name,
      })),
    },
  ];
}

/**
 * Which collection the panel is showing.
 *
 * The chosen one, or the first. A selection can outlive the collection it names
 * — a grant revoked between loads, a collection deleted in another tab — and
 * falling back to the first shows the caller a real collection rather than an
 * empty panel they cannot explain. Null only when they hold none at all.
 */
export function selectedCollection(
  collections: CollectionSummary[],
  chosen: string | null,
): CollectionSummary | null {
  return (
    collections.find((collection) => collection.id === chosen) ?? collections[0] ?? null
  );
}

/** The works in one collection, from the pairs every fetch produced. */
export function worksIn(placed: PlacedWork[], collectionId: string | null): PlacedWork[] {
  if (collectionId === null) return [];
  return placed.filter((entry) => entry.collectionId === collectionId);
}

/**
 * Why the strip has nothing in it, or null when it has something.
 *
 * Two different sentences, and the tiles are what make the distinction worth
 * drawing: an empty strip beside a WORKS tile reading 12 is this collection
 * being empty, not the account being new.
 */
export function describeEmpty(input: {
  totalWorks: number;
  shownWorks: number;
}): string | null {
  if (input.shownWorks > 0) return null;
  return input.totalWorks === 0 ? DASHBOARD_COPY.empty : DASHBOARD_COPY.emptyCollection;
}

/**
 * The dashboard's numbers.
 *
 * Every one is counted from something the registry actually holds. There is no
 * portfolio value and no 30-day change, because ONDO stores neither a price nor
 * a history of prices — a tile that invented one would be the most confidently
 * wrong thing on the screen.
 */
export function toTiles(input: {
  collections: CollectionSummary[];
  works: WorkSummary[];
}): Tile[] {
  const onLoan = input.works.filter((work) => work.state?.onLoan === true);
  // Counted off the chain of title, because it is the only thing the registry
  // now has to say about a work. There is no queue to be awaiting.
  const verified = input.works.filter((work) => work.verifiedCount > 0).length;

  return [
    {
      key: "works",
      label: DASHBOARD_COPY.worksRegistered,
      value: String(input.works.length),
      note: describeSpread(input.works),
    },
    {
      key: "loan",
      label: DASHBOARD_COPY.onLoan,
      value: String(onLoan.length),
      note: describeVenues(onLoan),
    },
    {
      key: "verified",
      label: DASHBOARD_COPY.verified,
      value: String(verified),
      note: verified > 0 ? "Signed by somebody" : null,
    },
    {
      key: "collections",
      label: DASHBOARD_COPY.collections,
      value: String(input.collections.length),
      note: null,
    },
  ];
}

/**
 * The first few works, in the order the registry returned them.
 *
 * NOT "the most recent": `WorkSummary` carries no timestamp, so recency is not
 * a thing this client can sort on, and a strip labelled "recent" that was
 * ordered arbitrarily would be a small lie told every time the page loads.
 * "View all" is beside it for the rest.
 */
export function toStripCards(input: {
  placed: PlacedWork[];
  /** '/collector/me/work' — where a card links to. Threaded, never assembled here. */
  workBase: string;
  /** Signed image URLs by work id. A work missing from this has no photograph to show. */
  images: Map<string, string>;
  limit: number;
}): StripCard[] {
  return input.placed.slice(0, input.limit).map(({ work }) => ({
    id: work.id,
    href: `${input.workBase}/${work.id}`,
    title: work.title,
    attribution: work.year
      ? `${work.attributedName} · ${work.year}`
      : work.attributedName,
    medium: work.medium,
    imageUrl: input.images.get(work.id) ?? null,
  }));
}

/**
 * What has happened lately, out of the recorded feed.
 *
 * Merged across every collection the caller holds, because a feed is addressed
 * by one subject and a dashboard is about all of them. Deduplicated by id so
 * the merge is order-independent — no activity can land in two collection feeds
 * today, and this is what keeps that from silently mattering if one ever does.
 *
 * Two kinds of row are dropped rather than guessed at, and both are recorded
 * history being wider than this build: an act it has no name for, and a
 * timestamp it cannot read. See `describeType`.
 */
export function toActivity(input: {
  entries: ActivityEntry[];
  /** Names by id, for the label a share deliberately does not carry. */
  collections: CollectionSummary[];
  /** '/collector/me/work' — where a row about a work goes. */
  workBase: string;
  /**
   * The reader's own ONDO id, so a row they caused reads "by you".
   *
   * Null while the account is still loading, which reads as the actor's own
   * name — true, if a little formal, and never wrong the way guessing would be.
   */
  viewerOndoId: string | null;
  now: Date;
  limit: number;
}): ActivityRow[] {
  const names = new Map(
    input.collections.map((collection) => [collection.id, collection.name]),
  );
  const unique = new Map<string, ActivityEntry>();
  for (const entry of input.entries) {
    if (!unique.has(entry.id)) unique.set(entry.id, entry);
  }

  return [...unique.values()]
    .flatMap((entry) => {
      const act = describeAct(entry.type, entry.historyKind);
      if (!act) return [];

      const age = describeAge(entry.createdAt, input.now);
      // A row whose date cannot be read is not recent activity. Dropping it
      // beats dating it "just now" and being wrong by a decade.
      if (age === null) return [];

      return [
        {
          id: entry.id,
          title: act.title,
          detail: detailFor(entry, names, input.viewerOndoId, age),
          glyph: act.glyph,
          // Only a work has a screen of its own. There is no per-collection
          // route — `hrefs.collection` is one destination with no id — so a
          // collection row has nowhere to go. The link is a snapshot's link:
          // the work may since have moved or stopped being visible, and it can
          // land on the record's error state, exactly as the strip's can.
          href: activityHref(entry, input.workBase),
          at: Date.parse(entry.createdAt),
        },
      ];
    })
    .sort((a, b) => b.at - a.at)
    .slice(0, input.limit)
    .map(({ at: _at, ...row }) => row);
}

/**
 * What an act is called and what it looks like, or null for one this build has
 * never heard of.
 *
 * The parameter is a `string` on purpose. `ActivityEntry.type` is TYPED as an
 * `ActivityType`, but it arrived over the wire from an API that may be deployed
 * ahead of this bundle, and TypeScript narrows nothing about that. So the guard
 * is real rather than a tautology, and `ACTIVITY_TITLES` stays a total record.
 *
 * Dropping matches what `activity.service.ts` does with a verb it does not
 * recognise. The cost, stated plainly: for the length of a deploy skew the
 * newest row can be silently missing, so "recent" reads a little stale. The
 * alternative is a row reading "Activity recorded", which names no act at all —
 * the same vague claim the tiles refuse to make.
 */

/**
 * "Fat Car · by Sarah Kim · 2 days ago".
 *
 * Three facts in the order a reader wants them: what it was about, who did it,
 * and when. Each part is dropped rather than filled in when it is not known —
 * a row with no label reads as the act and its age, which is true, where
 * "Untitled" would be invented.
 *
 * The actor is on every activity type and not only the verifications, because
 * the question "who did this" is the same question on a shared collection. It
 * was simply never printed.
 */
function detailFor(
  entry: ActivityEntry,
  names: Map<string, string>,
  viewerOndoId: string | null,
  age: string,
): string {
  const label = labelFor(entry, names);
  const who =
    entry.actor === null
      ? null
      : entry.actor.ondoId === viewerOndoId
        ? `${ACTIVITY_BY.prefix} ${ACTIVITY_BY.self}`
        : `${ACTIVITY_BY.prefix} ${entry.actor.displayName}`;

  return [label, who, age].filter((part) => part !== null).join(" · ");
}

/**
 * What the row is about, in words.
 *
 * The feed's own label first — it is a snapshot of the title as it read at the
 * time, which is what an audit line should say. A share carries none by design:
 * `payloadFor` records the level granted and never the grantee, because
 * `ondo.share_collection` makes "no such principal" and "not permitted" the same
 * answer. So the collection's own name stands in, and nothing here can recover
 * who it was shared with.
 *
 * Null when there is still no name to give. The row then reads as the act and
 * its age, which is true, rather than as "Untitled", which is invented.
 */
function labelFor(entry: ActivityEntry, names: Map<string, string>): string | null {
  if (entry.subject.label !== null) return entry.subject.label;
  if (entry.subject.kind === "collection") return names.get(entry.subject.id) ?? null;

  return null;
}

/**
 * "2 days ago", "3 wks ago", "1 mo ago".
 *
 * Null for anything that is not a timestamp, so a malformed `createdAt` drops
 * its row instead of dating it from `NaN`. Months and years are approximated
 * from days on purpose — this is a "how long ago", and a caller who needs the
 * exact date has it on the work's own record.
 */
export function describeAge(iso: string, now: Date): string | null {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;

  // Clamped at zero: a clock skew of a few seconds should read as "just now",
  // not as a negative age or a date in the future.
  const seconds = Math.max(0, (now.getTime() - then) / 1000);
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return "just now";
  if (minutes < 60) return unitsAgo(minutes, "min");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return unitsAgo(hours, "hr");

  const days = Math.floor(hours / 24);
  if (days < 7) return unitsAgo(days, "day");
  if (days < 30) return unitsAgo(Math.floor(days / 7), "wk");
  if (days < 365) return unitsAgo(Math.floor(days / 30), "mo");

  return unitsAgo(Math.floor(days / 365), "yr");
}

function unitsAgo(count: number, unit: string): string {
  return `${String(count)} ${unit}${count === 1 ? "" : "s"} ago`;
}

/**
 * Where the works are, counted as places rather than as rows.
 *
 * A work whose `state` is withheld is not counted — it is somewhere, but not
 * somewhere this caller was shown, and folding it into the total would inflate
 * the count with a place nobody can name.
 */
function describeSpread(works: WorkSummary[]): string | null {
  const places = new Set(
    works.flatMap((work) => {
      if (work.state === null || work.state.onLoan) return [];
      const place = work.state.placeText?.trim();
      if (place) return [place];

      return work.state.locationKind ? [LOCATION_LABELS[work.state.locationKind]] : [];
    }),
  );

  if (places.size === 0) return null;
  return places.size === 1 ? "In 1 location" : `Across ${String(places.size)} locations`;
}

/** The venue when a single work is out, the count when several are. */
function describeVenues(onLoan: WorkSummary[]): string | null {
  const venues = new Set(
    onLoan.flatMap((work) => {
      const venue = work.state?.loanVenue?.trim();
      return venue ? [venue] : [];
    }),
  );

  if (venues.size === 0) return null;
  if (venues.size === 1) return [...venues][0] ?? null;

  return `${String(venues.size)} venues`;
}

/**
 * The newest collections first, capped at `limit`.
 *
 * Which feeds are worth a request: the activity panel reads at most
 * `ACTIVITY_COLLECTION_BUDGET` collection feeds beside the personal one, and
 * the ones most recently made are the ones most likely to be moving.
 */
export function newestCollections(
  collections: readonly CollectionSummary[],
  limit: number,
): CollectionSummary[] {
  return [...collections]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, limit);
}
